import React, { useState, useEffect } from "react";
import { Loader2, Send, CheckCircle, AlertCircle, PlusCircle, Trash2 } from "lucide-react";
import { apiFetch } from "../api";
import { ensureSocketConnection } from "../websocket/socketUtils";
import { useWebSocket } from "../websocket/WebSocketContext";

export default function SMSForm({ setIsLoading, setSuccess, setError, API_BASE_URL, loginId, deliveryMode }) {
  const { client, connected } = useWebSocket();
  const [smsVendor, setSmsVendor] = useState("");
  const [smsVendorsList, setSmsVendorsList] = useState([]);
  const [sms_bulk_type, setSmsBulkType] = useState(true);
  const [mobileNoInput, setMobileNoInput] = useState(""); // single or bulk
  const [smsTemplate, setSmsTemplate] = useState("");
  const [appName, setAppName] = useState("");
  const [smsData, setSmsData] = useState([{ key: "", value: "" }]);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [successLocal, setSuccessLocal] = useState("");
  const [errorLocal, setErrorLocal] = useState("");
  const [appNamesList, setAppNamesList] = useState([]);

  // PAYTM specific fields
  const [templateId, setTemplateId] = useState("");
  const [msgType, setMsgType] = useState("TEXT");

  const fetchVendorNames = async () => {
    setIsLoadingLocal(true);
    if (setError) setError('');

    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          if (setError) setError('Socket connection failed.');
          setIsLoadingLocal(false);
          return;
        }

        const topic = `/topic/sms/config/getAll/${loginId}`;
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const payload = JSON.parse(msg.body);
            const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

            if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
              const appNames = [...data.Response.map(item => item).filter(Boolean)];
              setSmsVendorsList(appNames);
              setSmsVendor(prev => prev || appNames[0]);
            } else {
              if (setError) setError('Failed to load vendors via socket.');
            }
          } catch (e) {
            console.error('Failed to parse vendor list socket response', e);
          } finally {
            subscription.unsubscribe();
            setIsLoadingLocal(false);
          }
        });

        // Publish request with filter in body
        client.publish({
          destination: '/app/sms/config/getAll',
          body: JSON.stringify({ isSmsVendor: true })
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/sms/config/getAll?isSmsVendor=true`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
          const appNames = [...data.Response.map(item => item).filter(Boolean)];
          setSmsVendorsList(appNames);
          setSmsVendor(prev => prev || appNames[0]);
        } else {
          if (setError) setError('Failed to load app names. Invalid response format.');
        }
        setIsLoadingLocal(false);
      }
    } catch (err) {
      console.error('Error fetching app names:', err);
      if (setError) setError('Failed to load app names. Network error or API issue.');
      setSmsVendorsList([]);
      setSmsVendor('');
      setIsLoadingLocal(false);
    }
  };

  // Fetch app names & vendors on mount
  useEffect(() => {
    // 1. Fetch Vendors (assuming this function is already refactored to handle socket/api internally)
    fetchVendorNames();

    // 2. Fetch SMS App Names
    const fetchSmsAppNames = async () => {
      try {
        if (deliveryMode === 'socket') {
          // --- WEBSOCKET LOGIC ---
          try {
            await ensureSocketConnection(client);
          } catch (connError) {
            console.error("Socket connection failed for app names:", connError);
            return;
          }

          const topic = `/topic/getSmsAppName/${loginId}`;
          const subscription = client.subscribe(topic, (msg) => {
            try {
              const payload = JSON.parse(msg.body);
              // Handle potential double-stringification
              const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

              if (data.STATUS === "SUCCESS" && Array.isArray(data.Response)) {
                const names = data.Response.map(item => item.appName).filter(Boolean);
                setAppNamesList(names);

                // Set default app name if not already set
                // Note: accessing state inside useEffect closure can be stale, 
                // so we use the functional update pattern or just check the current render scope
                setAppName(prev => prev || (names.length > 0 ? names[0] : ''));
              }
            } catch (e) {
              console.error('Failed to parse SMS app name socket response', e);
            } finally {
              subscription.unsubscribe();
            }
          });

          // Publish request
          client.publish({
            destination: '/app/getSmsAppName',
            body: JSON.stringify({})
          });

        } else {
          // --- API LOGIC ---
          const res = await apiFetch(`/getSmsAppName`);
          const data = await res.json();

          if (data.STATUS === "SUCCESS" && Array.isArray(data.Response)) {
            const names = data.Response.map(item => item.appName).filter(Boolean);
            setAppNamesList(names);

            setAppName(prev => prev || (names.length > 0 ? names[0] : ''));
          }
        }
      } catch (err) {
        console.error("Failed to fetch app names:", err);
      }
    };

    fetchSmsAppNames();

  }, [API_BASE_URL, deliveryMode, client, loginId]);

  // auto-clear messages
  useEffect(() => {
    if (successLocal || errorLocal) {
      const t = setTimeout(() => {
        setSuccessLocal("");
        setErrorLocal("");
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [successLocal, errorLocal]);

  const handleSendSMS = async () => {
    // 1. Set Loading
    setIsLoadingLocal(true);
    if (typeof setIsLoading === "function") setIsLoading(true);
    setSuccessLocal("");
    setErrorLocal("");

    // 2. Validation
    if (!mobileNoInput || !smsTemplate || !appName) {
      setErrorLocal("Please fill Mobile No, Template, and App Name.");
      setIsLoadingLocal(false);
      if (typeof setIsLoading === "function") setIsLoading(false);
      return;
    }

    // 3. Construct Payload (Shared Logic)
    const smsDataObj = {};
    smsData.forEach(({ key, value }) => {
      if (key.trim()) smsDataObj[key] = value;
    });

    let payload;
    const numbers = mobileNoInput.split(/[,]+/).map((n) => n.trim()).filter((n) => n);

    if (smsVendor === "PAYTM") {
      const bulkSmsArr = numbers.map((num) => ({
        mobileNo: num,
        smsTemplate,
        appName,
        templateId: templateId || "",
        smsData: Object.keys(smsDataObj).length ? smsDataObj : null
      }));

      if (sms_bulk_type) {
        payload = {
          appName,
          smsVendor: "PAYTM",
          sms_bulk_type,
          bulkSms: bulkSmsArr
        };
      } else {
        payload = {
          appName,
          smsVendor: "PAYTM",
          sms_bulk_type,
          bulkSms: bulkSmsArr.slice(0, 1)
        };
      }
    } else {
      if (sms_bulk_type) {
        const bulkSmsArr = numbers.map((num) => ({
          mobileNo: num,
          smsTemplate,
          appName,
          smsData: Object.keys(smsDataObj).length ? smsDataObj : null
        }));

        payload = {
          smsVendor,
          sms_bulk_type,
          appName,
          bulkSms: bulkSmsArr
        };
      } else {
        payload = {
          smsVendor,
          sms_bulk_type,
          appName,
          sms: {
            mobileNo: mobileNoInput.trim(),
            smsTemplate,
            appName,
            smsData: Object.keys(smsDataObj).length ? smsDataObj : null
          }
        };
      }
    }

    // 4. Send Request (Socket vs API)
    try {
      if (deliveryMode === 'socket') {
        // --- WEBSOCKET LOGIC ---
        try {
          await ensureSocketConnection(client);
        } catch (connError) {
          console.error(connError);
          setErrorLocal('Socket connection failed.');
          setIsLoadingLocal(false);
          if (typeof setIsLoading === "function") setIsLoading(false);
          return;
        }

        const topic = `/topic/sendSms/${loginId}`;
        const subscription = client.subscribe(topic, (msg) => {
          try {
            const data = JSON.parse(msg.body);

            if (data.STATUS === "SUCCESS") {
              setSuccessLocal("SMS queued successfully!");
              // Reset Form
              setMobileNoInput("");
              setSmsTemplate("");
              setAppName("");
              setSmsData([{ key: "", value: "" }]);
              setTemplateId("");
            } else {
              setErrorLocal(`Failed: ${data.MESSAGE || "Unknown error"}`);
            }
          } catch (e) {
            console.error('Failed to parse SMS socket response', e);
            setErrorLocal('Failed to parse server response.');
          } finally {
            subscription.unsubscribe();
            setIsLoadingLocal(false);
            if (typeof setIsLoading === "function") setIsLoading(false);
          }
        });

        client.publish({
          destination: '/app/sendSms',
          body: JSON.stringify(payload)
        });

      } else {
        // --- API LOGIC ---
        const response = await apiFetch(`/sendSms`, {
          method: "POST",
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.STATUS === "SUCCESS") {
          setSuccessLocal("SMS queued successfully!");
          setMobileNoInput("");
          setSmsTemplate("");
          setAppName("");
          setSmsData([{ key: "", value: "" }]);
          setTemplateId("");
        } else {
          setErrorLocal(`Failed: ${data.MESSAGE || "Unknown error"}`);
        }
        setIsLoadingLocal(false);
        if (typeof setIsLoading === "function") setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      setErrorLocal("Network or server error.");
      setIsLoadingLocal(false);
      if (typeof setIsLoading === "function") setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Status messages */}
      {(successLocal || errorLocal) && (
        <div
          className={`p-4 rounded-xl flex items-center space-x-2 ${successLocal
            ? "bg-green-500/20 border border-green-500/50 text-green-200"
            : "bg-red-500/20 border border-red-500/50 text-red-200"
            }`}
        >
          {successLocal ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{successLocal || errorLocal}</span>
        </div>
      )}

      {/* Vendor dropdown */}
      <div>
        <label className="block text-white/80 mb-2">SMS Vendor</label>
        <select
          value={smsVendor}
          onChange={(e) => setSmsVendor(e.target.value)}
          className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
        >
          {smsVendorsList.length > 0 ? (
            smsVendorsList.map((vendor) => (
              <option key={vendor} value={vendor}>
                {vendor}
              </option>
            ))
          ) : (
            <>
              <option value="Y">Y</option>
              <option value="N">N</option>
              <option value="PAYTM">PAYTM</option>
            </>
          )}
        </select>
      </div>

      {/* Bulk toggle */}
      <div>
        <label className="block text-white/80 mb-2">Bulk SMS?</label>
        <input
          type="checkbox"
          checked={sms_bulk_type}
          onChange={(e) => setSmsBulkType(e.target.checked)}
        />{" "}
        Yes
      </div>

      {/* Mobile Number input */}
      <div>
        <label className="block text-white/80 mb-2">
          {sms_bulk_type ? "Mobile Numbers (one per line or comma separated)" : "Mobile Number"}
        </label>
        <textarea
          value={mobileNoInput}
          onChange={(e) => setMobileNoInput(e.target.value)}
          rows={sms_bulk_type ? 4 : 1}
          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white resize-none"
        />
      </div>

      {/* Template / Content */}
      <div>
        <label className="block text-white/80 mb-2">SMS Template / Content</label>
        <textarea
          value={smsTemplate}
          onChange={(e) => setSmsTemplate(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
        />
      </div>

      {/* PAYTM-specific inputs (template_id, msg_type) */}
      {smsVendor === "PAYTM" && (
        <div>
          <label className="block text-white/80 mb-2">PAYTM Template ID</label>
          <input
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            placeholder="e.g. 1707175931234430516"
            className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          />

          <label className="block text-white/80 mt-3 mb-2">Message Type</label>
          <select
            value={msgType}
            onChange={(e) => setMsgType(e.target.value)}
            className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
          >
            <option value="TEXT">TEXT</option>
            <option value="UNICODE">UNICODE</option>
          </select>

          <p className="text-white/60 text-sm mt-2">For PAYTM vendor the payload will be sent as <code>bulkSms: [&#123; smsData: &#123; msg_type, content, receiver, tag_name, template_id &#125; &#125;]</code></p></div>
      )}

      {/* App Name with type + dropdown */}
      <div>
        <label className="block text-white/80 mb-2">App Name</label>
        <input
          list="appNameOptions"
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          placeholder="Type or select app name..."
          className="w-full px-4 py-3 bg-black/60 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
        />
        <datalist id="appNameOptions">
          {appNamesList.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </div>


      {/* SMS Data (kept for non-PAYTM vendors) */}
      {smsVendor !== "PAYTM" && (
        <div>
          <label className="block text-white/80 mb-2">SMS Data Variables</label>
          {smsData.map((pair, idx) => (
            <div key={idx} className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                placeholder="Key"
                value={pair.key}
                onChange={(e) => {
                  const newData = [...smsData];
                  newData[idx].key = e.target.value;
                  setSmsData(newData);
                }}
                className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white"
              />
              <input
                type="text"
                placeholder="Value"
                value={pair.value}
                onChange={(e) => {
                  const newData = [...smsData];
                  newData[idx].value = e.target.value;
                  setSmsData(newData);
                }}
                className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white"
              />
              <button
                onClick={() => {
                  const newData = smsData.filter((_, i) => i !== idx);
                  setSmsData(newData);
                }}
                className="p-2 text-red-400 hover:text-red-200"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setSmsData([...smsData, { key: "", value: "" }])}
            className="flex items-center space-x-1 text-blue-400 hover:text-blue-200"
          >
            <PlusCircle size={16} /> <span>Add Variable</span>
          </button>
        </div>
      )}

      {/* Send Button */}
      <button
        onClick={handleSendSMS}
        disabled={isLoadingLocal}
        className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold"
      >
        {isLoadingLocal ? (
          <div className="flex items-center justify-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin" /> <span>Sending...</span>
          </div>
        ) : (
          <div className="flex items-center justify-center space-x-2">
            <Send className="w-5 h-5" /> <span>Send SMS</span>
          </div>
        )}
      </button>
    </div>
  );
}